import React, { useEffect, useState } from "react";
import {
  TextField, Grid, Typography, Button, MenuItem, FormControl,
  RadioGroup, FormControlLabel, Radio, Checkbox, Box, Select, Divider,
} from "@mui/material";
import { SideBarWidth } from "../../utils/SideBarWidth";
import { Colors } from "../../Theme/Theme";
import styled from "@emotion/styled";
import { Forms } from "../../api/requests/Forms";
import { FormOption } from "../../api/requests/FormOptions";
import GetValidatedTokenData from "../../utils/helper";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ShieldIcon from "@mui/icons-material/Shield";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";

const StyledTextField = styled(TextField)({
  "& .MuiInputBase-input": { fontFamily: "'Inter', sans-serif", color: "#0F172A", fontSize: "14px" },
  "& .MuiInputLabel-root": { fontFamily: "'Inter', sans-serif", color: "#94A3B8", fontSize: "13px" },
  "& label.Mui-focused": { color: Colors.insuranceGreen },
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    "& fieldset": { borderColor: "#E2E8F0" },
    "&:hover fieldset": { borderColor: Colors.insuranceGreen },
    "&.Mui-focused fieldset": { borderColor: Colors.insuranceGreen },
  },
  "& .MuiFormHelperText-root": { color: "#DC2626", fontSize: "11px" },
});

const StyledFormControl = styled(FormControl)({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8, fontSize: "14px",
    "& fieldset": { borderColor: "#E2E8F0" },
    "&:hover fieldset": { borderColor: Colors.insuranceGreen },
    "&.Mui-focused fieldset": { borderColor: Colors.insuranceGreen },
  },
});

const HospiCash = () => {
  const [formData, setFormData] = useState({
    first_name: "", last_name: "", email: "", phone_number: "",
    date_of_birth: "", country: "", address: "", city: "", zip: "",
    gender: "", shortcode: "", hospital: "", medication: "",
    hospitalization: "", issue: "", budget: "", insurance: "",
    date_of_admitting: "", terms_accepted: false,
  });
  const [formOptions, setFormOptions] = useState({});

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleCheckboxChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.checked });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const allowedFields = ["first_name", "last_name", "email", "phone_number", "date_of_birth", "country", "address", "type"];
    const directFields = {};
    const userinfoMeta = {};
    Object.keys(formData).forEach((key) => {
      if (allowedFields.includes(key)) directFields[key] = formData[key];
      else userinfoMeta[key] = formData[key];
    });
    const payload = {
      ...directFields, type: "hospital_cash",
      consents_meta: { marketing_emails: true, terms_accepted: formData?.terms_accepted },
      userinfo_meta: { ...userinfoMeta, preferred_language: "en", timezone: "UTC-5" },
      form_meta: { source: "web", campaign: "spring2024" },
    };
    try {
      const response = await Forms.createFormUser(payload);
      alert(response?.message || "Something went wrong.");
    } catch (error) {
      alert("Something went wrong.");
    } finally {
      setFormData({
        first_name: "", last_name: "", email: "", phone_number: "",
        date_of_birth: "", country: "", address: "", city: "", zip: "",
        gender: "", shortcode: formData.shortcode, hospital: "", medication: "",
        hospitalization: "", issue: "", budget: "", insurance: "",
        date_of_admitting: "", terms_accepted: false,
      });
    }
  };

  useEffect(() => {
    const response = GetValidatedTokenData();
    if (response?.userPayload?.shortcode) {
      setFormData((prev) => ({ ...prev, shortcode: response.userPayload.shortcode }));
    }
    const getFormOptionsFields = async () => {
      try {
        const { data } = await FormOption.getFormOptions("hospital_cash");
        if (data?.length > 0) {
          const opts = {};
          data.forEach((item) => { opts[item.form_field] = item.options?.values || []; });
          setFormOptions((prev) => ({ ...prev, ...opts }));
        }
      } catch (error) {}
    };
    getFormOptionsFields();
  }, []);

  return (
    <Box sx={{
      flexGrow: 1, width: { xs: "100%", md: `calc(100% - ${SideBarWidth}px)` },
      ml: { md: `${SideBarWidth}px` }, minHeight: "100vh", backgroundColor: Colors.bgColor,
    }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <LocalHospitalIcon sx={{ fontSize: 22, color: Colors.insuranceGreen }} />
            <Typography sx={{ fontWeight: 700, color: Colors.primaryTextColor, fontSize: { xs: "20px", md: "24px" }, letterSpacing: "-0.02em" }}>
              HospiCash
            </Typography>
          </Box>
          <Typography sx={{ color: Colors.secondaryColor, fontSize: "14px" }}>
            Secure your health and finances with hospital cash benefits.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Left info panel */}
          <Grid item xs={12} md={5}>
            <Box sx={{
              backgroundColor: Colors.white, borderRadius: "12px", border: `1px solid ${Colors.greenBorder}`,
              p: { xs: 3, md: 4 }, height: "100%",
            }}>
              <Typography sx={{ fontWeight: 700, color: Colors.primaryTextColor, fontSize: { xs: "20px", md: "26px" }, mb: 1.5, lineHeight: 1.2 }}>
                Hospital Cash{" "}
                <Box component="span" sx={{ color: Colors.insuranceGreen }}>Benefit Plan</Box>
              </Typography>
              <Typography sx={{ color: Colors.secondaryColor, fontSize: "14px", mb: 3, lineHeight: 1.7 }}>
                Financial safety net designed to cover your hospital expenses and protect your savings during medical emergencies.
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {[
                  { icon: <LocalHospitalIcon sx={{ fontSize: 20 }} />, label: "Cashless hospitalization coverage" },
                  { icon: <MedicalServicesIcon sx={{ fontSize: 20 }} />, label: "Daily cash allowance during hospital stay" },
                  { icon: <ShieldIcon sx={{ fontSize: 20 }} />, label: "No medical check-up required" },
                  { icon: <MonitorHeartIcon sx={{ fontSize: 20 }} />, label: "Coverage from day one" },
                ].map((item, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: "8px", backgroundColor: Colors.greenSoft,
                      display: "flex", alignItems: "center", justifyContent: "center", color: Colors.insuranceGreen, flexShrink: 0,
                    }}>
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontSize: "13px", fontWeight: 500, color: Colors.primaryTextColor }}>{item.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Right form */}
          <Grid item xs={12} md={7}>
            <Box component="form" onSubmit={handleSubmit} sx={{
              backgroundColor: Colors.white, borderRadius: "12px", border: `1px solid ${Colors.dividerColor}`,
              p: 3,
            }}>
              <Typography sx={{ fontWeight: 600, color: Colors.primaryTextColor, fontSize: "16px", mb: 0.5 }}>
                Application Form
              </Typography>
              <Typography sx={{ color: Colors.secondaryColor, mb: 2, fontSize: "13px" }}>
                Complete your details to enroll in the HospiCash plan.
              </Typography>
              <Divider sx={{ borderColor: Colors.dividerColor, mb: 2.5 }} />

              <Grid container spacing={2} textAlign="left">
                <Grid item xs={12} sm={6}>
                  <StyledTextField size="small" fullWidth label="First Name" value={formData.first_name} name="first_name" required onChange={handleChange} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField size="small" fullWidth label="Last Name" value={formData.last_name} name="last_name" required onChange={handleChange} />
                </Grid>
                <Grid item xs={12}>
                  <StyledTextField size="small" fullWidth label="Email" name="email" type="email" value={formData.email} required onChange={handleChange} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField size="small" fullWidth label="Phone Number" type="number" name="phone_number" required value={formData.phone_number} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField size="small" fullWidth label="Date of Birth" name="date_of_birth" type="date" required value={formData.date_of_birth} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField size="small" fullWidth label="Country" name="country" required value={formData.country} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField size="small" fullWidth label="City" name="city" required value={formData.city} onChange={handleChange} />
                </Grid>
                <Grid item xs={12}>
                  <StyledTextField size="small" fullWidth label="Address" name="address" required value={formData.address} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField size="small" fullWidth label="Pin Code" name="zip" type="text" required value={formData.zip} onChange={handleChange} inputProps={{ maxLength: 6 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 500, color: Colors.primaryTextColor, mb: 0.5 }}>Gender</Typography>
                  <RadioGroup row name="gender" onChange={handleChange} value={formData.gender}>
                    {["Male", "Female", "Other"].map((g) => (
                      <FormControlLabel key={g} value={g} control={<Radio size="small" sx={{ "&.Mui-checked": { color: Colors.insuranceGreen } }} />} label={<Typography sx={{ fontSize: "13px" }}>{g}</Typography>} />
                    ))}
                  </RadioGroup>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 500, color: Colors.primaryTextColor, mb: 0.5 }}>Select Hospital</Typography>
                  <StyledFormControl fullWidth>
                    <Select size="small" fullWidth name="hospital" onChange={handleChange} value={formData.hospital} displayEmpty sx={{ fontSize: "14px" }}>
                      <MenuItem value="">Select Hospital</MenuItem>
                      {formOptions?.hospital_cash?.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </StyledFormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 500, color: Colors.primaryTextColor, mb: 0.5 }}>On Medication?</Typography>
                  <RadioGroup row name="medication" onChange={handleChange} value={formData.medication}>
                    {["Yes", "No"].map((v) => (
                      <FormControlLabel key={v} value={v} control={<Radio size="small" sx={{ "&.Mui-checked": { color: Colors.insuranceGreen } }} />} label={<Typography sx={{ fontSize: "13px" }}>{v}</Typography>} />
                    ))}
                  </RadioGroup>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox name="terms_accepted" onChange={handleCheckboxChange} checked={formData.terms_accepted} size="small" sx={{ "&.Mui-checked": { color: Colors.insuranceGreen } }} />}
                    label={<Typography sx={{ fontSize: "13px", color: Colors.secondaryColor }}>I accept the Terms and Conditions</Typography>}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button type="submit" variant="contained" sx={{
                    backgroundColor: Colors.insuranceGreen, color: "#FFFFFF", fontWeight: 600,
                    py: 1.2, px: 4, borderRadius: "8px", fontSize: "14px",
                    boxShadow: "0 2px 6px rgba(5,150,105,0.2)",
                    "&:hover": { backgroundColor: Colors.insuranceGreenDark },
                  }}>
                    Submit Application
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default HospiCash;
